import { queryOptions, useMutation } from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';

import {
  NewSemenRequest,
  ODSemenResponse,
  PulledSemenResponse,
  PullODSemenRequest,
  PullSemenRequest,
  RejectDiscardSemenRequest,
  ReturnSemenRequest,
  ScheduledDonorResponse,
  ScheduledSemenResponse,
  SemenMutationResult,
  SslQcData,
  SslQcMutationResult,
  SslQcRequest,
  UpdateDSSuggestedRequest,
  UpdateSemenRequest,
} from './types';

export type {
  NewSemenRequest as NewSemenInput,
  ODSemenResponse as ODSemenItem,
  PullODSemenRequest,
  PullSemenRequest,
  PulledSemenResponse as PulledSemenItem,
  RejectDiscardSemenRequest,
  ReturnSemenRequest,
  ScheduledDonorResponse as ScheduledDonorItem,
  ScheduledSemenResponse as ScheduledSemenItem,
  SemenMutationResult,
  UpdateDSSuggestedRequest,
  UpdateSemenRequest as UpdateSemenInput,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(token: AccessToken): HeadersInit {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchGet<T>(
  token: AccessToken,
  path: string,
  params?: URLSearchParams,
): Promise<T> {
  const url = params
    ? `${getBaseUrl()}/api/v1/semen-pull/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/v1/semen-pull/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchMutation<T>(
  token: AccessToken,
  path: string,
  method: 'POST' | 'PUT',
  body: unknown,
): Promise<T> {
  const url = `${getBaseUrl()}/api/v1/semen-pull/${path}`;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(token),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchPulledSemen(
  token: AccessToken,
  params: {
    date: string;
    fertilizationSiteId: string;
  },
): Promise<PulledSemenResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('date', params.date);
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  return fetchGet<PulledSemenResponse[]>(token, 'pulled', searchParams);
}

async function fetchScheduledDonors(
  token: AccessToken,
  params: {
    fertilizationSiteId: string;
    donorScheduleDate: string;
    sireId: string;
    semenId?: string | null;
    accountId?: string | null;
    semenType?: string | null;
    dsSemenLocId?: string | null;
  },
): Promise<ScheduledDonorResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  searchParams.set('donorScheduleDate', params.donorScheduleDate);
  searchParams.set('sireId', params.sireId);
  if (params.semenId) searchParams.set('semenId', params.semenId);
  if (params.accountId) searchParams.set('accountId', params.accountId);
  if (params.semenType) searchParams.set('semenType', params.semenType);
  if (params.dsSemenLocId)
    searchParams.set('dsSemenLocId', params.dsSemenLocId);
  return fetchGet<ScheduledDonorResponse[]>(
    token,
    'scheduled-donors',
    searchParams,
  );
}

async function fetchUnfinishedScheduledDonors(
  token: AccessToken,
  params: {
    donorScheduleDate: string;
    fertSiteId: string;
  },
): Promise<ScheduledDonorResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorScheduleDate', params.donorScheduleDate);
  searchParams.set('fertSiteId', params.fertSiteId);
  return fetchGet<ScheduledDonorResponse[]>(
    token,
    'unfinished-scheduled-donors',
    searchParams,
  );
}

async function fetchODSemen(
  token: AccessToken,
  params: {
    fertSiteId: string;
    semenName?: string;
    accountName?: string;
    caneCode?: string;
    colCode?: string;
  },
): Promise<ODSemenResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertSiteId', params.fertSiteId);
  if (params.semenName) searchParams.set('semenName', params.semenName);
  if (params.accountName) searchParams.set('accountName', params.accountName);
  if (params.caneCode) searchParams.set('caneCode', params.caneCode);
  if (params.colCode) searchParams.set('colCode', params.colCode);
  return fetchGet<ODSemenResponse[]>(token, 'od-semen', searchParams);
}

async function fetchODSimilarSemen(
  token: AccessToken,
  params: {
    fertSiteId: string;
    semenName?: string;
    accountName?: string;
    caneCode?: string;
    colCode?: string;
  },
): Promise<ODSemenResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertSiteId', params.fertSiteId);
  if (params.semenName) searchParams.set('semenName', params.semenName);
  if (params.accountName) searchParams.set('accountName', params.accountName);
  if (params.caneCode) searchParams.set('caneCode', params.caneCode);
  if (params.colCode) searchParams.set('colCode', params.colCode);
  return fetchGet<ODSemenResponse[]>(token, 'od-similar-semen', searchParams);
}

export function scheduledSemenV2Options(
  token: AccessToken,
  params: {
    date: string;
    fertilizationSiteId: string;
  },
) {
  return queryOptions({
    queryKey: ['semen-pull', 'scheduled', 'v2', params],
    enabled: !!token,
    async queryFn() {
      const searchParams = new URLSearchParams();
      searchParams.set('date', params.date);
      searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
      const url = `${getBaseUrl()}/api/v2/semen-pull/scheduled?${searchParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: getHeaders(token),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return (await response.json()) as ScheduledSemenResponse[];
    },
  });
}

export const pulledSemenOptions = (
  token: AccessToken,
  params: {
    date: string;
    fertilizationSiteId: string;
  },
) =>
  queryOptions({
    queryKey: ['semen-pull', 'pulled', params],
    enabled: !!token,
    async queryFn() {
      return fetchPulledSemen(token, params);
    },
  });

export const scheduledDonorsOptions = (
  token: AccessToken,
  params: {
    fertilizationSiteId: string;
    donorScheduleDate: string;
    sireId: string;
    semenId?: string | null;
    accountId?: string | null;
    semenType?: string | null;
    dsSemenLocId?: string | null;
  },
) =>
  queryOptions({
    queryKey: ['semen-pull', 'scheduled-donors', params],
    enabled: !!token,
    async queryFn() {
      return fetchScheduledDonors(token, params);
    },
  });

export const unfinishedScheduledDonorsOptions = (
  token: AccessToken,
  params: {
    donorScheduleDate: string;
    fertSiteId: string;
  },
) =>
  queryOptions({
    queryKey: ['semen-pull', 'unfinished-scheduled-donors', params],
    enabled: !!token,
    async queryFn() {
      return fetchUnfinishedScheduledDonors(token, params);
    },
  });

export const odSemenOptions = (
  token: AccessToken,
  params: {
    fertSiteId: string;
    semenName?: string;
    accountName?: string;
    caneCode?: string;
    colCode?: string;
  },
) =>
  queryOptions({
    queryKey: ['semen-pull', 'od-semen', params],
    enabled: !!token,
    async queryFn() {
      return fetchODSemen(token, params);
    },
  });

export const odSimilarSemenOptions = (
  token: AccessToken,
  params: {
    fertSiteId: string;
    semenName?: string;
    accountName?: string;
    caneCode?: string;
    colCode?: string;
  },
) =>
  queryOptions({
    queryKey: ['semen-pull', 'od-similar-semen', params],
    enabled: !!token,
    async queryFn() {
      return fetchODSimilarSemen(token, params);
    },
  });

async function fetchSslQcData(
  token: AccessToken,
  params: {
    date: string;
    siteId: string;
  },
): Promise<SslQcData | null> {
  const searchParams = new URLSearchParams();
  searchParams.set('date', params.date);
  searchParams.set('siteId', params.siteId);
  return fetchGet<SslQcData | null>(token, 'ssl-qc', searchParams);
}

async function saveSslQcData(
  token: AccessToken,
  request: SslQcRequest,
): Promise<SslQcMutationResult> {
  return fetchMutation<SslQcMutationResult>(token, 'ssl-qc', 'PUT', request);
}

export const sslQcOptions = (
  token: AccessToken,
  params: { date: string; siteId: string },
) =>
  queryOptions({
    queryKey: ['semen-pull', 'ssl-qc', params],
    enabled: !!token,
    async queryFn() {
      return fetchSslQcData(token, params);
    },
  });

export function useSaveSslQcMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (request: SslQcRequest) => saveSslQcData(token, request),
  });
}

async function pullSemen(
  token: AccessToken,
  request: PullSemenRequest,
): Promise<SemenMutationResult> {
  return fetchMutation<SemenMutationResult>(token, 'pull', 'POST', request);
}

async function returnSemen(
  token: AccessToken,
  request: ReturnSemenRequest,
): Promise<SemenMutationResult> {
  return fetchMutation<SemenMutationResult>(token, 'return', 'POST', request);
}

async function rejectDiscardSemen(
  token: AccessToken,
  request: RejectDiscardSemenRequest,
): Promise<SemenMutationResult> {
  return fetchMutation<SemenMutationResult>(
    token,
    'reject-discard',
    'POST',
    request,
  );
}

async function createNewSemenFn(
  token: AccessToken,
  newSemen: NewSemenRequest,
): Promise<SemenMutationResult> {
  return fetchMutation<SemenMutationResult>(token, '', 'POST', newSemen);
}

async function updateSemenFn(
  token: AccessToken,
  updateSemenData: UpdateSemenRequest,
): Promise<SemenMutationResult> {
  return fetchMutation<SemenMutationResult>(token, '', 'PUT', updateSemenData);
}

async function updateDSSuggestedFn(
  token: AccessToken,
  request: UpdateDSSuggestedRequest,
): Promise<SemenMutationResult> {
  return fetchMutation<SemenMutationResult>(
    token,
    'ds-suggested',
    'PUT',
    request,
  );
}

async function pullODSemenFn(
  token: AccessToken,
  request: PullODSemenRequest,
): Promise<SemenMutationResult> {
  return fetchMutation<SemenMutationResult>(token, 'pull-od', 'POST', request);
}

// React Query mutation hooks
export function useCreateNewSemenMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (newSemen: NewSemenRequest) =>
      createNewSemenFn(token, newSemen),
  });
}

export function useUpdateSemenMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (updateSemenData: UpdateSemenRequest) =>
      updateSemenFn(token, updateSemenData),
  });
}

export function usePullSemenMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (request: PullSemenRequest) => pullSemen(token, request),
  });
}

export function useReturnSemenMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (request: ReturnSemenRequest) => returnSemen(token, request),
  });
}

export function useRejectDiscardSemenMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (request: RejectDiscardSemenRequest) =>
      rejectDiscardSemen(token, request),
  });
}

export function useUpdateDSSuggestedMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (request: UpdateDSSuggestedRequest) =>
      updateDSSuggestedFn(token, request),
  });
}

export function usePullODSemenMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (request: PullODSemenRequest) => pullODSemenFn(token, request),
  });
}
